import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, LogOut, Menu, User, X } from 'lucide-react';
import { LyrraOneLogo } from '@/components/brand/LyrraOneLogo';
import { LyrraOneRegionUrlBadge } from '@/components/public/LyrraOneRegionUrlBadge';
import '@/styles/lyrraone-public-header.css';
import {
  lockPublicMobileMenuScroll,
  MOBILE_PUBLIC_DRAWER_OVERLAY_STYLE,
  MOBILE_PUBLIC_DRAWER_PANEL_STYLE,
} from '@/lib/mobile-public-drawer-styles';

export type LoPublicHeaderCopy = {
  nav: {
    solutions: string;
    features: string;
    marketplace: string;
    demo: string;
    blog: string;
    pricing: string;
    contact: string;
    accessibility: string;
    webinars: string;
  };
  auth: { login: string; register: string };
  user: { dashboard: string; profile: string; logout: string };
};

type LinkRenderProps = {
  href: string;
  className: string;
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  'aria-current'?: 'page';
};

export type LoPublicHeaderProps = {
  copy: LoPublicHeaderCopy;
  isActive: (href: string) => boolean;
  renderLink: (props: LinkRenderProps) => ReactNode;
  langSelector: ReactNode;
  showWebinarsNav?: boolean;
  user?: {
    displayName: string;
    email?: string;
    dashboardHref: string;
    profileHref: string;
    onLogout: () => void;
  } | null;
  loginHref: string;
  registerHref: string;
};

export function LoPublicHeader({
  copy,
  isActive,
  renderLink,
  langSelector,
  showWebinarsNav = false,
  user,
  loginHref,
  registerHref,
}: LoPublicHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const solutionLinks = [
    { href: '/features', label: copy.nav.features },
    { href: '/demo', label: copy.nav.demo },
    { href: '/store', label: copy.nav.marketplace },
    { href: '/accessibility', label: copy.nav.accessibility },
  ];

  const mainLinks = [
    { href: '/pricing', label: copy.nav.pricing },
    ...(showWebinarsNav ? [{ href: '/webinaires', label: copy.nav.webinars }] : []),
    { href: '/blog', label: copy.nav.blog },
    { href: '/contact', label: copy.nav.contact },
  ];

  const solutionsActive = solutionLinks.some((l) => isActive(l.href));

  useEffect(() => {
    if (!menuOpen) return;
    return lockPublicMobileMenuScroll();
  }, [menuOpen]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setSolutionsOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  const navLinkClass = (active: boolean) =>
    `lo-public-header__nav-link${active ? ' is-active' : ''}`;

  const mobileLinkClass = (active: boolean) =>
    `lo-public-header__mobile-link${active ? ' is-active' : ''}`;

  return (
    <header className="lo-public-header" data-marketing-header>
      <div className="lo-public-header__bar">
        <div className="lo-public-header__inner">
          <div className="lo-public-header__brand">
            {renderLink({
              href: '/',
              className: 'inline-flex shrink-0',
              children: <LyrraOneLogo size="md" linkTo={false} />,
            })}
            <LyrraOneRegionUrlBadge />
          </div>

          <nav className="lo-public-header__nav" aria-label="Navigation principale">
            <div className="lo-public-header__dropdown" ref={dropdownRef}>
              <button
                type="button"
                className={`lo-public-header__nav-link lo-public-header__dropdown-trigger${solutionsActive ? ' is-active' : ''}`}
                aria-expanded={solutionsOpen}
                aria-haspopup="true"
                onClick={() => setSolutionsOpen((v) => !v)}
              >
                {copy.nav.solutions}
                <ChevronDown className={`h-4 w-4 transition-transform${solutionsOpen ? ' rotate-180' : ''}`} />
              </button>
              {solutionsOpen && (
                <div className="lo-public-header__dropdown-panel" role="menu">
                  {solutionLinks.map((item) => {
                    const active = isActive(item.href);
                    return (
                      <Fragment key={item.href}>
                        {renderLink({
                          href: item.href,
                          className: `lo-public-header__dropdown-link${active ? ' is-active' : ''}`,
                          active,
                          onClick: () => setSolutionsOpen(false),
                          children: item.label,
                        })}
                      </Fragment>
                    );
                  })}
                </div>
              )}
            </div>

            {mainLinks.map((item) => {
              const active = isActive(item.href);
              return (
                <Fragment key={item.href}>
                  {renderLink({
                    href: item.href,
                    className: navLinkClass(active),
                    active,
                    'aria-current': active ? 'page' : undefined,
                    children: item.label,
                  })}
                </Fragment>
              );
            })}

          </nav>

          <div className="lo-public-header__actions lo-public-header__actions--topbar">
            <div className="lo-public-header__lang lo-public-header__lang--topbar">{langSelector}</div>

            {user ? (
              <div className="relative hidden sm:block" ref={userMenuRef}>
                <button
                  type="button"
                  className="lo-public-header__user-btn"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="menu"
                  onClick={() => setUserMenuOpen((v) => !v)}
                >
                  <User className="h-4 w-4" />
                  <span className="max-w-[8rem] truncate">{user.displayName}</span>
                </button>
                {userMenuOpen && (
                  <div className="lo-public-header__user-menu" role="menu">
                    <div className="border-b border-gray-100 px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
                      {user.email && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
                    </div>
                    {renderLink({
                      href: user.dashboardHref,
                      className: 'block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50',
                      onClick: () => setUserMenuOpen(false),
                      children: copy.user.dashboard,
                    })}
                    {renderLink({
                      href: user.profileHref,
                      className: 'block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50',
                      onClick: () => setUserMenuOpen(false),
                      children: copy.user.profile,
                    })}
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      onClick={() => { setUserMenuOpen(false); user.onLogout(); }}
                    >
                      <LogOut className="h-4 w-4" />
                      {copy.user.logout}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {renderLink({
                  href: loginHref,
                  className: 'lo-public-header__login',
                  children: copy.auth.login,
                })}
                {renderLink({
                  href: registerHref,
                  className: 'lo-public-header__cta',
                  children: copy.auth.register,
                })}
              </>
            )}

            {user && renderLink({
              href: user.dashboardHref,
              className: 'lo-public-header__cta lo-public-header__cta--dashboard lo-public-header__cta--topbar',
              children: copy.user.dashboard,
            })}

            <button
              type="button"
              className="lo-public-header__menu-btn"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              aria-label="Menu de navigation"
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && createPortal(
        <>
          <div
            className="lo-public-header__overlay"
            style={MOBILE_PUBLIC_DRAWER_OVERLAY_STYLE}
            onClick={() => setMenuOpen(false)}
            aria-hidden
          />
          <aside
            id="mobile-menu"
            className="lo-public-header__drawer"
            style={MOBILE_PUBLIC_DRAWER_PANEL_STYLE}
            aria-label="Menu de navigation"
          >
            <div className="lo-public-header__drawer-head">
              <span className="lo-public-header__drawer-title">Menu</span>
              <button
                type="button"
                className="lo-public-header__menu-btn"
                aria-label="Fermer le menu"
                onClick={() => setMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="lo-public-header__drawer-body">
              <nav className="lo-public-header__mobile-nav" aria-label="Navigation mobile">
                <p className="lo-public-header__mobile-group-label">{copy.nav.solutions}</p>
                {solutionLinks.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <Fragment key={item.href}>
                      {renderLink({
                        href: item.href,
                        className: mobileLinkClass(active),
                        active,
                        onClick: () => setMenuOpen(false),
                        children: item.label,
                      })}
                    </Fragment>
                  );
                })}
                {mainLinks.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <Fragment key={item.href}>
                      {renderLink({
                        href: item.href,
                        className: mobileLinkClass(active),
                        active,
                        onClick: () => setMenuOpen(false),
                        children: item.label,
                      })}
                    </Fragment>
                  );
                })}
              </nav>
            </div>

            <footer className="lo-public-header__drawer-footer">
              <div className="lo-public-header__drawer-lang">
                {langSelector}
              </div>
              <div className="lo-public-header__mobile-actions">
                {user ? (
                  <>
                    {user.email && (
                      <p className="lo-public-header__drawer-user">
                        <span className="lo-public-header__drawer-user-name">{user.displayName}</span>
                        <span className="lo-public-header__drawer-user-email">{user.email}</span>
                      </p>
                    )}
                    {renderLink({
                      href: user.dashboardHref,
                      className: 'lo-public-header__cta lo-public-header__cta--drawer',
                      onClick: () => setMenuOpen(false),
                      children: copy.user.dashboard,
                    })}
                    <div className="lo-public-header__drawer-secondary-actions">
                      {renderLink({
                        href: user.profileHref,
                        className: mobileLinkClass(false),
                        onClick: () => setMenuOpen(false),
                        children: copy.user.profile,
                      })}
                      <button
                        type="button"
                        className="lo-public-header__mobile-link lo-public-header__mobile-link--danger"
                        onClick={() => { setMenuOpen(false); user.onLogout(); }}
                      >
                        <LogOut className="h-4 w-4" aria-hidden />
                        {copy.user.logout}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {renderLink({
                      href: loginHref,
                      className: 'lo-public-header__cta lo-public-header__cta--drawer lo-public-header__cta--drawer-outline',
                      onClick: () => setMenuOpen(false),
                      children: copy.auth.login,
                    })}
                    {renderLink({
                      href: registerHref,
                      className: 'lo-public-header__cta lo-public-header__cta--drawer',
                      onClick: () => setMenuOpen(false),
                      children: copy.auth.register,
                    })}
                  </>
                )}
              </div>
            </footer>
          </aside>
        </>,
        document.body,
      )}
    </header>
  );
}
